'use client';

import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useWallet } from './use-wallet';

/** Mirrors `notification_preferences` row — keep in sync with API route. */
export interface NotificationPreferencesRow {
  address: string;
  buyouts: boolean;
  forfeit_warnings: boolean;
  chat_mentions: boolean;
}

async function getAuthHeaders(): Promise<HeadersInit> {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  try {
    const privyToken = document.cookie
      .split('; ')
      .find((row) => row.startsWith('privy-token='))
      ?.split('=')[1];
    if (privyToken) {
      headers['Authorization'] = `Bearer ${privyToken}`;
    }
  } catch {
    // ignore
  }
  return headers;
}

export function useNotificationPreferences() {
  const { address, isConnected, isAddressLinked } = useWallet();
  const queryClient = useQueryClient();
  // Lowercased so a checksummed↔lowercase wallet flip can't fragment
  // the cache into two observers + two GETs (see also the matching
  // server-side cache key in `/api/notifications/preferences`).
  const key = ['notification-preferences', address?.toLowerCase() ?? null] as const;

  const query = useQuery({
    queryKey: key,
    queryFn: async (): Promise<NotificationPreferencesRow> => {
      if (!address) {
        return {
          address: '',
          buyouts: true,
          forfeit_warnings: true,
          chat_mentions: true,
        };
      }
      const headers = await getAuthHeaders();
      const res = await fetch(
        `/api/notifications/preferences?address=${encodeURIComponent(address)}`,
        { headers }
      );
      if (!res.ok) {
        return {
          address: address.toLowerCase(),
          buyouts: true,
          forfeit_warnings: true,
          chat_mentions: true,
        };
      }
      const json = await res.json();
      return json.preferences as NotificationPreferencesRow;
    },
    // Same `requireAuth` gate as `/api/notifications` — skip the fetch for
    // wallets Privy hasn't linked to the session (avoids 403 logs when
    // wagmi surfaces a MetaMask account the Privy user never signed
    // against).
    enabled: isConnected && !!address && isAddressLinked,
    staleTime: 60_000,
  });

  const patch = useMutation({
    mutationFn: async (
      prefs: Partial<
        Pick<NotificationPreferencesRow, 'buyouts' | 'forfeit_warnings' | 'chat_mentions'>
      >
    ) => {
      if (!address) return;
      const headers = await getAuthHeaders();
      const res = await fetch('/api/notifications/preferences', {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ address, ...prefs }),
      });
      if (!res.ok) throw new Error('Failed to update preferences');
      return res.json() as Promise<{ preferences: NotificationPreferencesRow }>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key });
    },
  });

  const setPreference = useCallback(
    (
      field: 'buyouts' | 'forfeit_warnings' | 'chat_mentions',
      value: boolean
    ) => {
      patch.mutate({ [field]: value });
    },
    [patch]
  );

  return {
    preferences: query.data,
    isLoading: query.isLoading,
    setPreference,
    isUpdating: patch.isPending,
  };
}
